// Ported verbatim from cartadigital-pos app/utils/printing/sticker-indexing.ts.
export interface IStickerIndexLine {
  lineId: string;
  index: number;
  amount: number;
  printE: number;
}

export interface IStickerIndexContext {
  totalLabelCount: number;
  startingIndexByLineId: Map<string, number>;
}

interface IComputeStickerIndexContextParams {
  lines: IStickerIndexLine[];
  isReprint?: boolean;
}

const toFiniteInteger = (value: unknown): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.trunc(n);
};

const clampInteger = (value: unknown, min: number, max: number): number => {
  const out = toFiniteInteger(value);
  return Math.min(max, Math.max(min, out));
};

/**
 * Compute order-wide sticker indexing context for $INDICE_ITEM rendering.
 *
 * The result is intentionally independent from print mode deltas:
 * - totalLabelCount is always computed from full eligible lines.
 * - startingIndexByLineId is reprint-aware:
 *   - reprint: starts from each line base index (1..N)
 *   - delta: starts after already printed units per line (printE offset)
 */
export function computeStickerIndexContext(
  params: IComputeStickerIndexContextParams
): IStickerIndexContext {
  const normalized = params.lines
    .map((line) => {
      const lineId = typeof line.lineId === 'string' ? line.lineId.trim() : '';
      return {
        lineId,
        index: toFiniteInteger(line.index),
        amount: Math.max(0, toFiniteInteger(line.amount)),
        printE: Math.max(0, toFiniteInteger(line.printE)),
      };
    })
    .filter((line) => line.lineId.length > 0);

  normalized.sort((a, b) => {
    if (a.index !== b.index) return a.index - b.index;
    return a.lineId.localeCompare(b.lineId);
  });

  const isReprint = params.isReprint === true;
  const startingIndexByLineId = new Map<string, number>();
  let cumulativeAmount = 0;

  for (const line of normalized) {
    if (line.amount <= 0) continue;

    const printedOffset = isReprint
      ? 0
      : clampInteger(line.printE, 0, line.amount);

    const lineBaseStart = cumulativeAmount + 1;
    startingIndexByLineId.set(line.lineId, lineBaseStart + printedOffset);
    cumulativeAmount += line.amount;
  }

  return {
    totalLabelCount: cumulativeAmount,
    startingIndexByLineId,
  };
}

export function resolveStickerLineStartIndex(
  context: IStickerIndexContext,
  lineId: string | null | undefined
): number {
  if (typeof lineId !== 'string' || lineId.trim().length === 0) return 1;
  const start = context.startingIndexByLineId.get(lineId);
  if (!Number.isFinite(start) || Number(start) < 1) return 1;
  return Number(start);
}
